import { useState, useEffect, useRef, useLayoutEffect } from 'react';

function useCustomHorizontalScrollbar(comparisonContentIsLoading, contentRef) {
  const customScrollbarRef = useRef(null);
  const thumbRef = useRef(null);
  const [isScrollable, setIsScrollable] = useState(false);

  const [thumbWidth, setThumbWidth] = useState(0);
  const [thumbLeft, setThumbLeft] = useState(0);

  // Calculate and update the width of the scrollbar thumb handle
  useLayoutEffect(() => {
    const updateThumbWidth = () => {
      if (contentRef.current && customScrollbarRef.current) {
        const contentScrollWidth = contentRef.current.scrollWidth;
        const contentClientWidth = contentRef.current.clientWidth;
        const scrollbarWidth = customScrollbarRef.current.clientWidth;
        const newThumbWidth = (contentClientWidth / contentScrollWidth) * scrollbarWidth;
        setThumbWidth(newThumbWidth || 0);
        const scrollable = contentScrollWidth > contentClientWidth;
        setIsScrollable(scrollable);
      }
    };

    updateThumbWidth();

    let resizeObserver;

    if (customScrollbarRef.current) {
      resizeObserver = new ResizeObserver(() => {
        updateThumbWidth();
      });
      resizeObserver.observe(customScrollbarRef.current);
    }

    window.addEventListener('resize', updateThumbWidth);

    return () => {
      window.removeEventListener('resize', updateThumbWidth);
      if (resizeObserver && customScrollbarRef.current) {
        resizeObserver.unobserve(customScrollbarRef.current);
      }
    };
  }, [comparisonContentIsLoading]);

  // Sync the position of the scrollbar thumb handle with the content scroll position
  useEffect(() => {
    const syncThumbPosition = () => {
      if (contentRef.current && customScrollbarRef.current && thumbRef.current) {
        const contentScrollLeft = contentRef.current.scrollLeft;
        const maxContentScroll = contentRef.current.scrollWidth - contentRef.current.clientWidth;
        const maxScrollbarScroll = customScrollbarRef.current.clientWidth - thumbWidth;
        const newThumbLeft = (contentScrollLeft / maxContentScroll) * maxScrollbarScroll || 0;
        setThumbLeft(newThumbLeft);
      }
    };

    const contentElement = contentRef.current;
    contentElement?.addEventListener('scroll', syncThumbPosition);

    syncThumbPosition();

    return () => {
      contentElement?.removeEventListener('scroll', syncThumbPosition);
    };
  }, [thumbWidth]);

  // Handle dragging of the scrollbar thumb handle
  useEffect(() => {
    const thumbElement = thumbRef.current;
    const contentElement = contentRef.current;

    if (!thumbElement || !contentElement) return;

    let isDragging = false;
    let initialMouseX = 0;
    let initialMouseY = 0;
    let initialScrollLeft = 0;
    let initialScrollTop = 0;

    const onMouseDown = (e) => {
      isDragging = true;
      initialMouseX = e.clientX;
      initialMouseY = e.clientY;
      initialScrollLeft = contentElement.scrollLeft;
      initialScrollTop = contentElement.scrollTop;
      document.body.style.userSelect = 'none';
      contentElement.style.cursor = 'grabbing';
    };

    const onMouseMove = (e) => {
      if (!isDragging) return;
      const deltaX = e.clientX - initialMouseX;
      const deltaY = e.clientY - initialMouseY;
      const maxContentScrollX = contentElement.scrollWidth - contentElement.clientWidth;
      const maxScrollbarScrollX = customScrollbarRef.current.clientWidth - thumbWidth;
      const scrollRatioX = maxContentScrollX / maxScrollbarScrollX;
      contentElement.scrollLeft = initialScrollLeft + deltaX * scrollRatioX;
      contentElement.scrollTop = initialScrollTop - deltaY;
    };

    const onMouseUp = () => {
      isDragging = false;
      document.body.style.userSelect = ''; // Re-enable text selection
      contentElement.style.cursor = 'grab';
    };

    thumbElement.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    // CLEAN UP so eslint is ok
    // eslint-disable-next-line consistent-return
    return () => {
      thumbElement.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [thumbWidth]);

  // Handle dragging of the content area for scrolling on desktop
  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    let isSwiping = false;
    let initialMouseX = 0;
    let initialMouseY = 0;
    let initialScrollLeft = 0;
    let initialScrollTop = 0;

    const onMouseDown = (e) => {
      if (e.target.tagName === 'BUTTON') return; // Ignore button clicks
      isSwiping = true;
      initialMouseX = e.clientX;
      initialMouseY = e.clientY;
      initialScrollLeft = contentElement.scrollLeft;
      initialScrollTop = contentElement.scrollTop;
      document.body.style.userSelect = 'none';
      contentElement.style.cursor = 'grabbing';
    };

    const onMouseMove = (e) => {
      if (!isSwiping) return;
      const deltaX = e.clientX - initialMouseX;
      const deltaY = e.clientY - initialMouseY;
      contentElement.scrollLeft = initialScrollLeft - deltaX;
      contentElement.scrollTop = initialScrollTop - deltaY;
    };

    const onMouseUp = () => {
      isSwiping = false;
      document.body.style.userSelect = '';
      contentElement.style.cursor = 'grab';
    };

    contentElement.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    // Set initial cursor style
    contentElement.style.cursor = 'grab';

    // eslint-disable-next-line consistent-return
    return () => {
      contentElement.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [comparisonContentIsLoading]);

  return {
    customScrollbarRef,
    thumbRef,
    thumbStyle: {
      width: `${thumbWidth}px`,
      transform: `translateX(${thumbLeft}px)`,
    },
    isScrollable,
  };
}

export default useCustomHorizontalScrollbar;
